/**
 * Maintenance-mode gating logic, driven entirely by `src/data/site-config.json`.
 *
 * This is pure and framework-free on purpose: `MaintenanceGate` (the React
 * component that actually redirects) and the unit tests both call
 * `getMaintenanceStatus` directly, so the decision logic is verified once and
 * used everywhere rather than re-implemented at each call site.
 */

import { parseIstDateTime } from './istDate'

export interface MaintenanceConfig {
  enabled: boolean
  startAt: string | null
  endAt: string | null
  message: string
}

export interface MaintenanceStatus {
  active: boolean
  message: string
  /** UTC epoch ms the window ends, when a valid `endAt` was given. */
  endAtMs: number | null
}

const INACTIVE = (message: string): MaintenanceStatus => ({ active: false, message, endAtMs: null })

/**
 * Decides whether maintenance mode is active right now.
 *
 * Rules, in order:
 *   1. `enabled: false` — never active.
 *   2. `enabled: true`, no `startAt`/`endAt` — active immediately (manual mode).
 *   3. `enabled: true` with a schedule — active only between `startAt` and
 *      `endAt` (an `endAt`-less schedule stays active from `startAt` onward).
 *   4. A schedule that fails to parse is treated as no schedule at all, and
 *      logged — a typo in the date string must make the site stay live, never
 *      lock visitors out by accident.
 */
export const getMaintenanceStatus = (config: MaintenanceConfig, now: number = Date.now()): MaintenanceStatus => {
  if (!config.enabled) return INACTIVE(config.message)

  if (config.startAt == null && config.endAt == null) {
    return { active: true, message: config.message, endAtMs: null }
  }

  const startAtMs = config.startAt == null ? null : parseIstDateTime(config.startAt)
  if (config.startAt != null && startAtMs === null) {
    console.error(
      `[maintenance] could not parse startAt "${config.startAt}" (expected "DD Mon YYYY, h:mm AM/PM") — ignoring the schedule, site stays live.`,
    )
    return INACTIVE(config.message)
  }

  const endAtMs = config.endAt == null ? null : parseIstDateTime(config.endAt)
  if (config.endAt != null && endAtMs === null) {
    console.error(
      `[maintenance] could not parse endAt "${config.endAt}" (expected "DD Mon YYYY, h:mm AM/PM") — ignoring the schedule, site stays live.`,
    )
    return INACTIVE(config.message)
  }

  if (startAtMs !== null && endAtMs !== null && startAtMs >= endAtMs) {
    console.error('[maintenance] startAt is not before endAt — ignoring the schedule, site stays live.')
    return INACTIVE(config.message)
  }

  const started = startAtMs === null || now >= startAtMs
  const ended = endAtMs !== null && now >= endAtMs
  return { active: started && !ended, message: config.message, endAtMs }
}
